import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MobileWorkflowTabs = ({ 
  activeStage, 
  onStageChange, 
  stages, 
  jobs, 
  onJobClick,
  onRecordCompletion 
}) => {
  const getStageIcon = (stageName) => {
    switch (stageName) {
      case 'Digital Printing': return 'Printer';
      case 'Cutting': return 'Scissors';
      case 'Stitching': return 'Shirt';
      default: return 'Package';
    }
  };

  const getStageJobs = (stageId) => {
    return jobs.filter(job => job.stage === stageId);
  };

  const currentStage = stages.find(stage => stage.id === activeStage);
  const currentJobs = getStageJobs(activeStage);

  return (
    <div className="md:hidden">
      {/* Stage Tabs */}
      <div className="flex bg-card border border-border rounded-lg p-1 mb-4">
        {stages.map((stage) => (
          <button
            key={stage.id}
            onClick={() => onStageChange(stage.id)}
            className={`flex-1 flex items-center justify-center space-x-2 py-3 px-2 rounded-md text-sm font-medium transition-smooth ${
              activeStage === stage.id
                ? 'bg-primary text-primary-foreground shadow-elevation-1'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Icon name={getStageIcon(stage.name)} size={16} />
            <span className="hidden sm:inline">{stage.name}</span>
          </button>
        ))}
      </div>

      {/* Stage Header */}
      {currentStage && (
        <div className="bg-card border border-border rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name={getStageIcon(currentStage.name)} size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">{currentStage.name}</h3>
                <p className="text-sm text-muted-foreground">{currentJobs.length} jobs</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-foreground">{currentStage.completionRate}%</p>
              <p className="text-xs text-muted-foreground">Complete</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-muted rounded-full h-2 mb-3">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${currentStage.completionRate}%` }}
            ></div>
          </div>

          {/* Operators */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2">Assigned Operators</h4>
            <div className="flex flex-wrap gap-2">
              {currentStage.operators.map((operator) => (
                <div key={operator.id} className="flex items-center space-x-2 bg-muted/50 rounded-full px-3 py-1">
                  <div className="w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-xs text-primary-foreground font-medium">
                      {operator.name.charAt(0)}
                    </span>
                  </div>
                  <span className="text-xs font-medium text-foreground">{operator.name}</span>
                  <div className={`w-2 h-2 rounded-full ${
                    operator.status === 'available' ? 'bg-success' : 
                    operator.status === 'busy' ? 'bg-warning' : 'bg-error'
                  }`}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Job Cards */}
      <div className="space-y-3">
        {currentJobs.map((job) => (
          <div
            key={job.id}
            className="bg-card border border-border rounded-lg p-4 shadow-elevation-1"
            onClick={() => onJobClick(job)}
          >
            {/* Job Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${
                  job.priority === 'high' ? 'bg-error' :
                  job.priority === 'medium' ? 'bg-warning' : 'bg-success'
                }`}></div>
                <h5 className="font-medium text-foreground">{job.designName}</h5>
              </div>
              <span className="text-xs text-muted-foreground">#{job.jobId}</span>
            </div>

            {/* Job Details Grid */}
            <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
              <div>
                <span className="text-muted-foreground">Fabric:</span>
                <span className="ml-2 font-medium text-foreground">{job.fabricMeters}m</span>
              </div>
              <div>
                <span className="text-muted-foreground">Pieces:</span>
                <span className="ml-2 font-medium text-foreground">{job.totalPieces}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Due:</span>
                <span className="ml-2 font-medium text-foreground">{job.dueDate}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Progress:</span>
                <span className="ml-2 font-medium text-foreground">{job.progress}%</span>
              </div>
            </div>

            {/* Size Distribution */}
            <div className="mb-3">
              <p className="text-xs text-muted-foreground mb-2">Sizes:</p>
              <div className="flex space-x-1">
                {Object.entries(job.sizeDistribution).map(([size, count]) => (
                  <div key={size} className="flex-1 text-center bg-muted rounded px-1 py-0.5">
                    <p className="text-xs font-medium text-foreground">{size}</p>
                    <p className="text-xs text-muted-foreground">{count}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-3">
              <div className="w-full bg-muted rounded-full h-1.5">
                <div 
                  className="bg-primary h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${job.progress}%` }}
                ></div>
              </div>
            </div>

            {/* Assigned Operator */}
            {job.assignedOperator && (
              <div className="mb-3 p-2 bg-muted/50 rounded">
                <div className="flex items-center space-x-2">
                  <Icon name="User" size={14} color="var(--color-muted-foreground)" />
                  <span className="text-sm text-foreground">{job.assignedOperator}</span>
                </div>
              </div>
            )}

            {/* Defects Alert */}
            {currentStage.name === 'Cutting' && job.defects > 0 && (
              <div className="mb-3 p-2 bg-error/10 rounded border border-error/20">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={14} color="var(--color-error)" />
                  <span className="text-xs text-error font-medium">{job.defects} defective pieces</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-2">
              {job.progress === 100 ? (
                <Button
                  variant="success"
                  size="sm"
                  fullWidth
                  iconName="Check"
                  iconSize={14}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRecordCompletion(job.id);
                  }}
                >
                  Mark Complete
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Play"
                    iconSize={14}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Start
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Pause"
                    iconSize={14}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Pause
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="MoreVertical"
                    iconSize={14}
                    onClick={(e) => e.stopPropagation()}
                  />
                </>
              )}
            </div>
          </div>
        ))}

        {/* Empty State */}
        {currentJobs.length === 0 && (
          <div className="bg-card border border-border rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name={getStageIcon(currentStage?.name)} size={24} color="var(--color-muted-foreground)" />
            </div>
            <h4 className="font-medium text-foreground mb-2">No jobs in {currentStage?.name}</h4>
            <p className="text-sm text-muted-foreground">Jobs will appear here when assigned to this stage</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileWorkflowTabs;